import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  FaClock,
  FaEnvelope,
  FaFacebookF,
  FaGlobe,
  FaInstagram,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaYoutube,
} from 'react-icons/fa';

import styles from '../../assets/styles/Footer.module.css';
import logo from '../../assets/images/logo.png';
import logo1 from '../../assets/images/logo1.png';
import {
  FOOTER_API_BASE,
  FOOTER_FALLBACK,
  normalizeFooterContent,
  toFooterAssetUrl,
} from '../../utils/footerConfig';

const footerFade = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.6, ease: 'easeOut' },
  },
};

const columnContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const columnItem = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: 'easeOut' },
  },
};

const socialIconMap = {
  facebook: FaFacebookF,
  instagram: FaInstagram,
  youtube: FaYoutube,
};

const contactIconMap = {
  location: FaMapMarkerAlt,
  phone: FaPhoneAlt,
  email: FaEnvelope,
  clock: FaClock,
  custom: FaGlobe,
};

const resolveHref = (item) => {
  if (item.href) return item.href;
  if (item.type === 'phone') return `tel:${item.value.replace(/\s+/g, '')}`;
  if (item.type === 'email') return `mailto:${item.value}`;
  return '';
};

const Footer = () => {
  const [footerData, setFooterData] = useState(FOOTER_FALLBACK);

  useEffect(() => {
    fetch(FOOTER_API_BASE)
      .then((res) => res.json())
      .then((json) => {
        if (json?.success) {
          setFooterData(normalizeFooterContent(json.data));
        }
      })
      .catch(() => {});
  }, []);

  const primaryLogoSrc = useMemo(
    () =>
      footerData.primaryLogoUrl ? toFooterAssetUrl(footerData.primaryLogoUrl) : logo,
    [footerData.primaryLogoUrl]
  );

  const secondaryLogoSrc = useMemo(
    () =>
      footerData.secondaryLogoUrl
        ? toFooterAssetUrl(footerData.secondaryLogoUrl)
        : logo1,
    [footerData.secondaryLogoUrl]
  );

  const linkSections = footerData.linkSections || [];
  const contactItems = footerData.contactItems || [];
  const socialLinks = footerData.socialLinks || [];
  const keywords = footerData.keywords || [];

  return (
    <motion.footer
      className={styles.footer}
      variants={footerFade}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
    >
      <div className={styles.overlay}></div>

      <div className="container-fluid">
        <motion.div
          className="row"
          variants={columnContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.div
            className="col-xl-3 col-lg-3 col-md-6 col-sm-6 col-12"
            variants={columnItem}
          >
            <div className={styles.taglineWrapper}>
              <p className={styles.tagline}>{footerData.aboutText}</p>
            </div>
            <div className={styles.logoWrapper}>
              <img src={primaryLogoSrc} alt="Hatha Yog Ashram" className={styles.logo} />
            </div>
          </motion.div>

          {linkSections.map((section, index) => (
            <motion.div
              key={`${section.title}-${index}`}
              className="col-xl col-lg-3 col-md-6 col-sm-6 col-12"
              variants={columnItem}
            >
              <h5 className={styles.title}>{section.title}</h5>
              <ul className={styles.list}>
                {(section.links || []).map((item, linkIndex) => (
                  <li key={`${item.path}-${linkIndex}`}>
                    <Link to={item.path || '/'}>{item.label}</Link>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}

          <motion.div
            className="col-xl-2 col-lg-3 col-md-6 col-sm-6 col-12"
            variants={columnItem}
          >
            <h5 className={styles.title}>{footerData.contactTitle}</h5>
            <ul className={styles.contact}>
              {contactItems.map((item, index) => {
                const Icon = contactIconMap[item.type] || FaGlobe;
                const href = resolveHref(item);

                return (
                  <li key={`${item.type}-${index}`}>
                    <Icon className={styles.icon} />
                    {href ? (
                      <a href={href} target={href.startsWith('http') ? '_blank' : undefined} rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}>
                        {item.value}
                      </a>
                    ) : (
                      <span>{item.value}</span>
                    )}
                  </li>
                );
              })}
            </ul>

            <div className={styles.logoWrapper}>
              <img
                src={secondaryLogoSrc}
                alt="Hatha Yog Ashram secondary"
                className={styles.logo1}
              />
            </div>

            {footerData.trustText && (
              <p className={styles.trustText}>{footerData.trustText}</p>
            )}

            <div className={styles.socials}>
              {socialLinks.map((item, index) => {
                const Icon = socialIconMap[item.platform?.toLowerCase()] || FaGlobe;
                return (
                  <motion.a
                    key={`${item.platform}-${index}`}
                    whileHover={{ scale: 1.15, rotate: 5 }}
                    whileTap={{ scale: 0.95 }}
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={item.platform}
                  >
                    <Icon />
                  </motion.a>
                );
              })}
            </div>
          </motion.div>
        </motion.div>

        <div className={styles.divider}></div>

        <motion.div
          className={styles.keywords}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          {keywords.map((keyword, index) => (
            <span key={`${keyword}-${index}`}>{keyword}</span>
          ))}
        </motion.div>

        <motion.div
          className={styles.bottom}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <p>
            © {new Date().getFullYear()} {footerData.copyrightText}
          </p>
        </motion.div>
      </div>
    </motion.footer>
  );
};

export default Footer;
